import { useState } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { X, Ticket, Loader2, AlertCircle } from 'lucide-react';
import { createTicket, TICKET_PRIORITY, TICKET_CATEGORY } from '../../services/supportService';
import { getUser } from '../../services/authService';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

const CreateTicket = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const user = getUser();

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: TICKET_PRIORITY.NORMAL,
    category: TICKET_CATEGORY.GENERAL
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.subject.trim()) {
      setError(t.support?.errors?.subjectRequired || 'Le sujet est requis');
      return;
    }

    if (!formData.description.trim()) {
      setError(t.support?.errors?.descriptionRequired || 'La description est requise');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const result = await createTicket({
        userId: user.id,
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        category: formData.category
      });

      if (result.success) {
        onCreated();
      } else {
        setError(result.message || t.support?.errors?.createFailed || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(err.message || t.support?.errors?.createFailed || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={t.support?.createTicket || 'Créer un ticket'} maxWidth="max-w-2xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t.support?.fields?.subject || 'Sujet'} *
          </label>
          <input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder={t.support?.placeholders?.subject || 'Décrivez brièvement votre problème'}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500"
            maxLength={255}
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t.support?.fields?.category || 'Catégorie'}
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500"
          >
            <option value="general">{t.support?.category?.general || 'Général'}</option>
            <option value="bug">{t.support?.category?.bug || 'Bug'}</option>
            <option value="feature">{t.support?.category?.feature || 'Fonctionnalité'}</option>
            <option value="billing">{t.support?.category?.billing || 'Facturation'}</option>
            <option value="account">{t.support?.category?.account || 'Compte'}</option>
            <option value="other">{t.support?.category?.other || 'Autre'}</option>
          </select>
        </div>

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t.support?.fields?.priority || 'Priorité'}
          </label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500"
          >
            <option value="low">{t.support?.priority?.low || 'Basse'}</option>
            <option value="normal">{t.support?.priority?.normal || 'Normale'}</option>
            <option value="high">{t.support?.priority?.high || 'Haute'}</option>
            <option value="urgent">{t.support?.priority?.urgent || 'Urgente'}</option>
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t.support?.fields?.description || 'Description'} *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder={t.support?.placeholders?.description || 'Décrivez votre problème en détail...'}
            rows={6}
            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-purple-500 resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            {t.common?.cancel || 'Annuler'}
          </Button>
          <Button
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t.common?.loading || 'Chargement...'}
              </>
            ) : (
              <>
                <Ticket className="w-4 h-4 mr-2" />
                {t.support?.submit || 'Envoyer'}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTicket;
